import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getPostBySlug, formatPostDate } from "@/lib/posts";

// Render VERBATIM body preserving newline-separated paragraphs and `## Subheadings`.
// Inline: **bold** and *italic*.
function renderInline(text, keyBase) {
  const nodes = [];
  let i = 0;
  let plainStart = 0;
  const flushPlain = (end) => {
    if (end > plainStart) nodes.push(text.slice(plainStart, end));
  };
  while (i < text.length) {
    // **bold**
    if (text[i] === "*" && text[i + 1] === "*") {
      const close = text.indexOf("**", i + 2);
      if (close !== -1) {
        flushPlain(i);
        nodes.push(
          <strong key={`${keyBase}-b-${i}`} style={{ fontWeight: 600 }}>
            {text.slice(i + 2, close)}
          </strong>
        );
        i = close + 2;
        plainStart = i;
        continue;
      }
    }
    // *italic*
    if (text[i] === "*") {
      const close = text.indexOf("*", i + 1);
      if (close !== -1) {
        flushPlain(i);
        nodes.push(
          <em key={`${keyBase}-i-${i}`}>{text.slice(i + 1, close)}</em>
        );
        i = close + 1;
        plainStart = i;
        continue;
      }
    }
    i++;
  }
  flushPlain(text.length);
  return nodes;
}

function renderBody(body) {
  const lines = body.split("\n");
  const out = [];
  lines.forEach((raw, idx) => {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) return;
    if (line.startsWith("## ")) {
      out.push(
        <h2
          key={`h-${idx}`}
          className="font-serif mt-12 mb-4 text-2xl md:text-3xl leading-tight"
          style={{ color: "#1B4332" }}
        >
          {renderInline(line.slice(3), `h-${idx}`)}
        </h2>
      );
      return;
    }
    out.push(
      <p
        key={`p-${idx}`}
        className="mt-5 text-base md:text-[17px] leading-[1.75]"
        style={{ color: "#2A2621" }}
      >
        {renderInline(line, `p-${idx}`)}
      </p>
    );
  });
  return out;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div data-testid={`page-blog-post-${post.slug}`}>
      <article className="container-refined pt-16 md:pt-24 pb-24 max-w-3xl">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-sm"
          style={{ color: "#1B4332" }}
          data-testid="blog-back-link"
        >
          <ArrowLeft size={14} /> Back to all posts
        </Link>

        <header className="mt-10">
          <span className="text-xs tracking-[0.22em] uppercase" style={{ color: "#6E6759" }} data-testid="blog-post-date">
            {formatPostDate(post.date)}
          </span>
          <h1
            className="font-serif mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            style={{ color: "#1B4332" }}
            data-testid="blog-post-title"
          >
            {post.title}
          </h1>
        </header>

        <div className="mt-8" data-testid="blog-post-body">
          {renderBody(post.body)}
        </div>
      </article>
    </div>
  );
}
